import type { ServiceManager } from '../service-manager';
import { AssetLoader, FontLoader, type AssetLoadState, type FontLoadState } from './assets';
import { Pipeline } from './pipeline';
import type { GlyphCache } from './pipeline/glyph/cache';
import type {
    ITextRenderer,
    TextRendererOptions,
    TextRenderInput,
    TextRenderOutput,
    TextRenderResolution,
    TextRenderSnapshot,
} from './types';
import { createFontRequests, isValidInput } from './utils';

type DependentServices = Pick<ServiceManager, 'font' | 'loading' | 'textEngine' | 'textRender'> & {
    /**
     * 编辑器实例内跨 Renderer 共享、由 TextRenderService 统一持有的字形模板缓存。
     */
    glyphCache: GlyphCache;
};

/**
 * 将未知异常归一化为 failed resolution。
 *
 * @param error - renderer 捕获的未知异常。
 * @returns 统一的失败结果。
 */
const failed = <T>(error: unknown): TextRenderResolution<T> => ({
    status: 'failed',
    error: error instanceof Error ? error : new Error(String(error)),
});

/**
 * 单个宿主持有的文字 renderer 实现。
 */
export class TextRenderer implements ITextRenderer {
    private readonly assetLoader: AssetLoader;
    private readonly fontLoader: FontLoader;

    /**
     * 当前 Renderer 独占的同步排版、绘制与输出缓存所有者。
     */
    private readonly pipeline: Pipeline;

    private disposed = false;

    /**
     * 公开调用的 latest-only 所有权世代号。
     *
     * 每次 `layout()`、`layoutAsync()` 或 `render()` 调用都会递增；`layoutAsync()` 在等待字体前捕获当前值，
     * 等待结束后仅在值未变化时发布结果。任何更晚的公开调用或 `dispose()` 都会令旧请求失效，使其返回
     * pending，从而避免异步完成的旧输入覆盖更新输入的缓存。
     *
     * `renderAsync()` 是导出专用的只读等待，不参与该所有权竞争，既不递增也不检查本值，原因见其注释。
     */
    private generation = 0;

    /**
     * 创建一个隔离的 renderer。
     *
     * Pipeline、TextBuffer、snapshot 和输出缓存仍由当前 Renderer 独占；只有位置无关 GlyphCache 由
     * TextRenderService 注入共享，避免不同文字图层重复提取相同字体轮廓。
     *
     * @param services - Renderer 实际依赖的编辑器服务。
     * @param options - renderer 级失效通知选项。
     * @returns TextRenderer 实例。
     */
    constructor(
        private readonly services: DependentServices,
        options: TextRendererOptions,
    ) {
        this.pipeline = new Pipeline(services.textEngine, services.glyphCache, options.layerId);

        this.assetLoader = new AssetLoader({
            setLoading: (loading) => {
                const ASSET_LOADING_KEY = 'text-renderer:assets';
                if (loading) {
                    services.loading.add(options.layerId, ASSET_LOADING_KEY);
                } else {
                    services.loading.remove(options.layerId, ASSET_LOADING_KEY);
                }
            },
            notifyInvalidated: () => options.onInvalidated?.(),
        });

        this.fontLoader = new FontLoader({
            fontService: services.font,
            getFontRevision: () =>
                services.textEngine.getEngine() ? (services.textEngine.getFontRevision?.() ?? 0) : null,
            notifyInvalidated: () => options.onInvalidated?.(),
        });
    }

    /**
     * 对完整普通文字输入执行同步排版。
     *
     * @param input - 独立于图层的文字渲染输入。
     * @returns 排版 snapshot 的显式四态结果。
     */
    layout(input: TextRenderInput): TextRenderResolution<TextRenderSnapshot> {
        this.generation += 1;
        return this.resolveLayout(input);
    }

    /**
     * 等待当前输入实际使用的已注册字体子集后执行排版。
     *
     * 未注册字体保留原字体族；该路径准备配置的 fallback，不等待 Text Engine，也不读取素材。
     * 等待期间若有更新的公开调用取得所有权，本次旧请求返回 pending，不能发布旧缓存。
     *
     * @param input - 独立于图层的文字渲染输入。
     * @returns 当前字体依赖进入终态后的排版四态结果。
     */
    async layoutAsync(input: TextRenderInput): Promise<TextRenderResolution<TextRenderSnapshot>> {
        const generation = ++this.generation;

        // 空输入和无效输入没有需要等待的字体，直接复用同步解析协议。
        if (this.disposed || !input.text || !isValidInput(input)) {
            return this.resolveLayout(input);
        }

        const fontState = await this.fontLoader.prepare(createFontRequests(input));

        // 字体 key 相同不代表完整输入相同，必须在 Renderer 层拒绝已被取代的请求。
        if (generation !== this.generation) {
            return { status: 'pending' };
        }

        return this.resolveLayout(input, fontState);
    }

    /**
     * 使用与 layout 相同的 snapshot 生成完整普通文字 SVG。
     *
     * @param input - 独立于图层的文字渲染输入。
     * @returns SVG、布局与共享 snapshot 的显式四态结果。
     */
    render(input: TextRenderInput): TextRenderResolution<TextRenderOutput> {
        this.generation += 1;
        return this.resolveRender(input);
    }

    /**
     * 等待当前输入实际使用的字体子集和素材后生成完整输出，供导出路径使用。
     *
     * 等待不包含 Text Engine；依赖进入终态后 Text Engine 仍未就绪时返回 pending。
     *
     * 不递增或检查 generation，不取消画布字体加载，也不接管素材 loading 与失效通知。
     * 导出与同步渲染、原子布局可以并行；Pipeline 按解析后的输入匹配缓存并同步生成结果。
     * 并发语义的背景见本目录 README。
     *
     * @param input - 独立于图层的文字渲染输入。
     * @returns 当前字体与素材依赖进入终态后的完整渲染四态结果；等待期间 Renderer 被释放时返回 pending。
     */
    async renderAsync(input: TextRenderInput): Promise<TextRenderResolution<TextRenderOutput>> {
        if (this.disposed) return failed(new Error('Text renderer has been disposed.'));
        // 空输入不能走 resolveRender 的 empty 分支：该分支会重置 FontLoader / Pipeline / AssetLoader，
        // 导出一个空文字图层会因此清掉画布正在使用的缓存。
        if (!input.text) return { status: 'empty' };
        if (!isValidInput(input)) return failed(new Error('Invalid text render input.'));

        // 字体和素材互不依赖，并行等待可避免串行增加完整输出的等待时间。
        const [fontState, assetState] = await Promise.all([
            this.fontLoader.prepareDetached(createFontRequests(input)),
            this.assetLoader.prepare(input),
        ]);

        if (this.disposed) return { status: 'pending' };

        return this.resolveRender(input, fontState, assetState);
    }

    /**
     * 使用给定依赖解析结果生成完整文字输出并发布缓存。
     *
     * @param input - 当前完整 Text Render Input。
     * @param preparedFontState - 异步路径已等待完成的字体状态；省略时立即读取当前字体状态。
     * @param preparedAssetState - 异步路径已等待完成的素材状态；省略时立即读取当前素材状态。
     * @returns 当前输入对应的完整渲染四态结果。
     */
    private resolveRender(
        input: TextRenderInput,
        preparedFontState?: FontLoadState,
        preparedAssetState?: AssetLoadState,
    ): TextRenderResolution<TextRenderOutput> {
        if (this.disposed) return failed(new Error('Text renderer has been disposed.'));
        if (!input.text) {
            this.resolveLayout(input);
            this.assetLoader.reset();
            return { status: 'empty' };
        }
        if (!isValidInput(input)) {
            return failed(new Error('Invalid text render input.'));
        }

        // 完整输出才拥有素材检查与对应的图层 loading 生命周期。
        const fontState = preparedFontState ?? this.fontLoader.ensure(createFontRequests(input));
        const assetState = preparedAssetState ?? this.assetLoader.ensure(input);
        if (fontState.status === 'pending' || assetState.status === 'pending') {
            return { status: 'pending' };
        }
        if (fontState.status === 'failed') {
            return failed(fontState.error);
        }
        if (assetState.status === 'failed') {
            return failed(assetState.error);
        }
        try {
            const output = this.pipeline.render(input, fontState.fontFamilies, assetState.assets);
            if (!output) return { status: 'pending' };
            return { status: 'ready', value: output };
        } catch (error) {
            return failed(error);
        }
    }

    /**
     * 使用给定字体解析结果执行同步排版并发布缓存。
     *
     * @param input - 当前完整 Text Render Input。
     * @param preparedFontState - 异步路径已等待完成的字体状态；省略时立即读取当前字体状态。
     * @returns 当前输入对应的排版四态结果。
     */
    private resolveLayout(
        input: TextRenderInput,
        preparedFontState?: FontLoadState,
    ): TextRenderResolution<TextRenderSnapshot> {
        if (this.disposed) return failed(new Error('Text renderer has been disposed.'));

        if (!input.text) {
            this.fontLoader.reset();
            this.pipeline.reset();
            return { status: 'empty' };
        }

        if (!isValidInput(input)) return failed(new Error('Invalid text render input.'));

        const fontState = preparedFontState ?? this.fontLoader.ensure(createFontRequests(input));
        if (fontState.status === 'pending') return { status: 'pending' };
        if (fontState.status === 'failed') return failed(fontState.error);

        try {
            const snapshot = this.pipeline.layout(input, fontState.fontFamilies);
            if (!snapshot) return { status: 'pending' };
            return { status: 'ready', value: snapshot };
        } catch (error) {
            return failed(error);
        }
    }

    /**
     * 幂等释放 renderer 持有的快照与 service 注册关系。
     *
     * @returns void。
     */
    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.generation += 1;
        this.pipeline.reset();
        this.assetLoader.dispose();
        this.fontLoader.dispose();
        this.services.textRender.releaseRenderer(this);
    }
}
